import { useState, useEffect, useRef } from 'react';
import { authAxios } from '../../contexts/AuthContext';
import { Plus, Edit, UserX, Users, UserCheck, KeyRound, Eye, EyeOff, CalendarClock, Upload, X, IdCard, AlertCircle, PauseCircle, ChevronRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { AdminHeader } from '../../components/AdminHeader';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VALIDITY_OPTIONS = [
  { value: '0', label: 'Unlimited' },
  { value: '1', label: '1 month' },
  { value: '3', label: '3 months' },
  { value: '6', label: '6 months' },
  { value: '12', label: '12 months' },
];

const EXTEND_OPTIONS = [1, 3, 6, 12];

const EMPTY_FORM = {
  username: '', password: '', name: '',
  phone: '', address: '', pan_number: '',
  aadhar_front_path: '', aadhar_back_path: '', pan_card_path: '',
  validity_months: '0',
};

function computeExpiryInfo(acc) {
  if (!acc.expires_at) return { text: 'Unlimited validity', tone: 'grey', expired: false };
  const now = new Date();
  const exp = new Date(acc.expires_at);
  const diffMs = exp - now;
  const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  if (days < 0) return { text: `Expired ${-days}d ago`, tone: 'red', expired: true };
  if (days <= 7) return { text: `${days}d left`, tone: 'amber', expired: false };
  if (days <= 30) return { text: `${days}d left`, tone: 'amber', expired: false };
  const months = Math.floor(days / 30);
  return { text: months >= 1 ? `${months}mo left` : `${days}d left`, tone: 'grey', expired: false };
}

function FileSlot({ label, path, onUpload, onClear, testid }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await authAxios.post(`${API}/upload`, fd);
      onUpload(data.path);
    } catch { toast.error('Upload failed'); }
    setBusy(false);
    e.target.value = '';
  };
  return (
    <div>
      <Label className="text-[11px] font-semibold uppercase tracking-wider text-[#8E8E93] mb-1.5 block">{label}</Label>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={handle} data-testid={`${testid}-input`} />
      {path ? (
        <div className="relative group aspect-[1.6] bg-[#F2F2F7] rounded-2xl overflow-hidden border border-[#E5E5EA]">
          <img src={`${API}/files/${path}?auth=${localStorage.getItem('access_token')}`} alt={label} className="w-full h-full object-cover" />
          <button type="button" onClick={onClear} data-testid={`${testid}-clear`}
            className="absolute top-1.5 right-1.5 w-7 h-7 bg-white/95 rounded-full flex items-center justify-center shadow">
            <X className="w-3.5 h-3.5 text-[#1C1C1E]" />
          </button>
        </div>
      ) : (
        <button type="button" onClick={() => ref.current?.click()} disabled={busy} data-testid={testid}
          className="w-full aspect-[1.6] border-2 border-dashed border-[#E5E5EA] bg-[#F7F7F7] rounded-2xl flex flex-col items-center justify-center text-[#8E8E93] hover:bg-[#F2F2F7] transition-colors">
          <Upload className="w-5 h-5 mb-1" strokeWidth={1.5} />
          <span className="text-[11px]">{busy ? 'Uploading...' : 'Upload'}</span>
        </button>
      )}
    </div>
  );
}

export default function AdminEmployees() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // 'add' | 'edit' | 'password' | 'extend' | 'view'
  const [editingAccount, setEditingAccount] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [extendMonths, setExtendMonths] = useState('3');

  useEffect(() => { fetchAccounts(); }, []);

  const fetchAccounts = async () => {
    try { const { data } = await authAxios.get(`${API}/admin/accounts`); setAccounts(data); }
    catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleCreate = async () => {
    if (!form.username || !form.password || !form.name) { toast.error('Name, username and password are required'); return; }
    setSubmitting(true);
    try {
      const payload = {
        username: form.username, password: form.password, name: form.name,
        phone: form.phone || null,
        address: form.address || null,
        pan_number: form.pan_number || null,
        aadhar_front_path: form.aadhar_front_path || null,
        aadhar_back_path: form.aadhar_back_path || null,
        pan_card_path: form.pan_card_path || null,
        validity_months: form.validity_months && form.validity_months !== '0' ? parseInt(form.validity_months, 10) : null,
      };
      await authAxios.post(`${API}/admin/accounts`, payload);
      toast.success('Account created');
      setDialog(null); setForm(EMPTY_FORM); fetchAccounts();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setSubmitting(false);
  };

  const handleUpdate = async () => {
    if (!editingAccount) return;
    setSubmitting(true);
    try {
      const payload = {};
      if (dialog === 'password') {
        if (!form.password) { toast.error('Enter new password'); setSubmitting(false); return; }
        payload.password = form.password;
      } else {
        if (form.name !== undefined) payload.name = form.name;
        payload.phone = form.phone || '';
        payload.address = form.address || '';
        payload.pan_number = form.pan_number || '';
        payload.aadhar_front_path = form.aadhar_front_path || '';
        payload.aadhar_back_path = form.aadhar_back_path || '';
        payload.pan_card_path = form.pan_card_path || '';
      }
      await authAxios.put(`${API}/admin/accounts/${editingAccount.id}`, payload);
      toast.success(dialog === 'password' ? 'Password changed' : 'Account updated');
      setDialog(null); setEditingAccount(null); setForm(EMPTY_FORM); fetchAccounts();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setSubmitting(false);
  };

  const toggleActive = async (acc) => {
    try {
      await authAxios.put(`${API}/admin/accounts/${acc.id}`, { is_active: !acc.is_active });
      toast.success(acc.is_active ? 'Account placed on hold' : 'Account activated');
      fetchAccounts();
    } catch { toast.error('Failed'); }
  };

  const handleExtend = async () => {
    if (!editingAccount) return;
    const months = parseInt(extendMonths, 10);
    if (!months || months <= 0) { toast.error('Pick months'); return; }
    setSubmitting(true);
    try {
      await authAxios.post(`${API}/admin/accounts/${editingAccount.id}/extend`, { months });
      toast.success(`Extended by ${months} month${months > 1 ? 's' : ''}`);
      setDialog(null); setEditingAccount(null); fetchAccounts();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setSubmitting(false);
  };

  const openEdit = (acc) => {
    setEditingAccount(acc);
    setForm({
      username: acc.username, password: '', name: acc.name,
      phone: acc.phone || '', address: acc.address || '', pan_number: acc.pan_number || '',
      aadhar_front_path: acc.aadhar_front_path || '',
      aadhar_back_path: acc.aadhar_back_path || '',
      pan_card_path: acc.pan_card_path || '',
      validity_months: '0',
    });
    setDialog('edit');
  };

  const openView = (acc) => { setEditingAccount(acc); setDialog('view'); };

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <AdminHeader />

      <div className="max-w-3xl mx-auto px-4 py-5 sm:py-8">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-2xl font-bold text-[#1C1C1E]">Employee Accounts</h1>
            <p className="text-[13px] text-[#8E8E93] mt-0.5">{accounts.length} total · tap any card for full details</p>
          </div>
          <Button data-testid="add-account-btn" className="h-9 px-4 bg-[#1C1C1E] text-white hover:bg-[#2C2C2E] rounded-full text-sm font-medium"
            onClick={() => { setDialog('add'); setForm(EMPTY_FORM); setShowPassword(false); }}>
            <Plus className="w-4 h-4 mr-1" strokeWidth={2} /> Add
          </Button>
        </div>

        {loading ? <p className="text-[#8E8E93]">Loading...</p> : accounts.length === 0 ? (
          <div className="bg-white rounded-2xl text-center p-8 shadow-sm">
            <Users className="w-8 h-8 text-[#C7C7CC] mx-auto mb-3" strokeWidth={1.5} />
            <p className="text-[#8E8E93] text-sm">No employee accounts yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {accounts.map((acc) => {
              const exp = computeExpiryInfo(acc);
              const held = !acc.is_active;
              return (
                <div key={acc.id} data-testid={`acc-card-${acc.username}`} className="bg-white rounded-2xl shadow-sm p-4">
                  <div className="flex items-center gap-3 mb-3 cursor-pointer" data-testid={`view-${acc.username}`} onClick={() => openView(acc)}>
                    <div className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 ${held ? 'bg-[#F2F2F7]' : exp.expired ? 'bg-red-50' : 'bg-green-50'}`}>
                      {held ? <PauseCircle className="w-5 h-5 text-[#8E8E93]" strokeWidth={1.5} /> :
                       exp.expired ? <AlertCircle className="w-5 h-5 text-red-500" strokeWidth={1.5} /> :
                       <UserCheck className="w-5 h-5 text-green-600" strokeWidth={1.5} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <p className="text-[15px] font-semibold text-[#1C1C1E] truncate">{acc.name}</p>
                        {held && <Badge className="bg-[#F2F2F7] text-[#8E8E93] border-0 text-[10px]">On Hold</Badge>}
                        {!held && exp.expired && <Badge className="bg-red-50 text-red-700 border-0 text-[10px]">Expired</Badge>}
                        {!held && !exp.expired && <Badge className="bg-green-50 text-green-700 border-0 text-[10px]">Active</Badge>}
                      </div>
                      <div className="flex items-center gap-2 flex-wrap text-[12px] text-[#8E8E93] mt-0.5">
                        <span>@{acc.username}</span>
                        {acc.phone && <span>· {acc.phone}</span>}
                        <span className="flex items-center gap-0.5">
                          <CalendarClock className="w-3 h-3" strokeWidth={1.5} />
                          <span className={exp.tone === 'red' ? 'text-red-600 font-medium' : exp.tone === 'amber' ? 'text-amber-600' : ''}>{exp.text}</span>
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-[#C7C7CC]" strokeWidth={1.5} />
                  </div>
                  <div className="flex items-center gap-1 flex-wrap border-t border-[#F2F2F7] pt-2.5">
                    <Button variant="ghost" size="sm" className="text-[#8E8E93] hover:text-[#1C1C1E] h-7 px-2 rounded-full text-[11px]"
                      data-testid={`change-pw-${acc.username}`}
                      onClick={(e) => { e.stopPropagation(); setEditingAccount(acc); setForm({ ...EMPTY_FORM, password: '' }); setShowPassword(false); setDialog('password'); }}>
                      <KeyRound className="w-3 h-3 mr-0.5" /> Password
                    </Button>
                    <Button variant="ghost" size="sm" className="text-[#8E8E93] hover:text-[#1C1C1E] h-7 px-2 rounded-full text-[11px]"
                      data-testid={`edit-acc-${acc.username}`}
                      onClick={(e) => { e.stopPropagation(); openEdit(acc); }}>
                      <Edit className="w-3 h-3 mr-0.5" /> Edit
                    </Button>
                    <Button variant="ghost" size="sm" className="text-[#8E8E93] hover:text-[#1C1C1E] h-7 px-2 rounded-full text-[11px]"
                      data-testid={`extend-${acc.username}`}
                      onClick={(e) => { e.stopPropagation(); setEditingAccount(acc); setExtendMonths('3'); setDialog('extend'); }}>
                      <CalendarClock className="w-3 h-3 mr-0.5" /> Extend
                    </Button>
                    <Button variant="ghost" size="sm"
                      className={`h-7 px-2 rounded-full text-[11px] ${held ? 'text-green-600' : 'text-amber-600'}`}
                      data-testid={`toggle-acc-${acc.username}`}
                      onClick={(e) => { e.stopPropagation(); toggleActive(acc); }}>
                      {held ? 'Activate' : 'Hold'}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create / Edit Dialog (shared form) */}
      <Dialog open={dialog === 'add' || dialog === 'edit'} onOpenChange={() => setDialog(null)}>
        <DialogContent className="bg-white max-w-[94vw] sm:max-w-2xl rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {dialog === 'add' ? 'New Employee Account' : `Edit @${editingAccount?.username}`}
            </DialogTitle>
            <DialogDescription className="text-[13px] text-[#8E8E93]">
              {dialog === 'add' ? 'Fill what you have now — photos & details are optional and can be added later.' : 'Update employee details. Username cannot be changed.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            {/* Essentials */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] font-semibold uppercase tracking-wider text-[#8E8E93] mb-1.5 block">Full Name *</Label>
                <Input data-testid="acc-name-input" className="h-11 bg-[#F2F2F7] border-0 rounded-2xl" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Employee name" />
              </div>
              {dialog === 'add' && (
                <div>
                  <Label className="text-[11px] font-semibold uppercase tracking-wider text-[#8E8E93] mb-1.5 block">Username *</Label>
                  <Input data-testid="acc-username-input" className="h-11 bg-[#F2F2F7] border-0 rounded-2xl" value={form.username} onChange={(e) => setForm(f => ({ ...f, username: e.target.value.toLowerCase().replace(/\s/g, '') }))} placeholder="e.g. raju, master1" />
                </div>
              )}
              {dialog === 'add' && (
                <div className="sm:col-span-2">
                  <Label className="text-[11px] font-semibold uppercase tracking-wider text-[#8E8E93] mb-1.5 block">Password *</Label>
                  <div className="relative">
                    <Input data-testid="acc-password-input" type={showPassword ? 'text' : 'password'} className="h-11 bg-[#F2F2F7] border-0 rounded-2xl pr-12" value={form.password} onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))} placeholder="Set a password" />
                    <button type="button" className="absolute right-4 top-1/2 -translate-y-1/2 text-[#8E8E93]" onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              )}
              {dialog === 'add' && (
                <div className="sm:col-span-2">
                  <Label className="text-[11px] font-semibold uppercase tracking-wider text-[#8E8E93] mb-1.5 block">Account Validity</Label>
                  <Select value={form.validity_months} onValueChange={(v) => setForm(f => ({ ...f, validity_months: v }))}>
                    <SelectTrigger data-testid="validity-select" className="h-11 bg-[#F2F2F7] border-0 rounded-2xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {VALIDITY_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-[#8E8E93] mt-1">After this period the account is placed on hold automatically. You can always extend it.</p>
                </div>
              )}
              <div>
                <Label className="text-[11px] font-semibold uppercase tracking-wider text-[#8E8E93] mb-1.5 block">Phone</Label>
                <Input data-testid="acc-phone-input" className="h-11 bg-[#F2F2F7] border-0 rounded-2xl" value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="10-digit mobile (optional)" />
              </div>
              <div>
                <Label className="text-[11px] font-semibold uppercase tracking-wider text-[#8E8E93] mb-1.5 block">PAN Number</Label>
                <Input data-testid="acc-pan-input" className="h-11 bg-[#F2F2F7] border-0 rounded-2xl uppercase" value={form.pan_number} onChange={(e) => setForm(f => ({ ...f, pan_number: e.target.value.toUpperCase() }))} placeholder="ABCDE1234F (optional)" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-[11px] font-semibold uppercase tracking-wider text-[#8E8E93] mb-1.5 block">Current Address</Label>
                <Textarea data-testid="acc-address-input" className="bg-[#F2F2F7] border-0 rounded-2xl min-h-[70px]" value={form.address} onChange={(e) => setForm(f => ({ ...f, address: e.target.value }))} placeholder="House/street, city, PIN (optional)" />
              </div>
            </div>

            {/* KYC docs */}
            <div className="pt-2">
              <div className="flex items-center gap-1.5 mb-2">
                <IdCard className="w-3.5 h-3.5 text-[#8E8E93]" strokeWidth={1.5} />
                <p className="text-[11px] font-semibold uppercase tracking-wider text-[#8E8E93]">KYC Documents (optional)</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <FileSlot label="Aadhar Front" path={form.aadhar_front_path} onUpload={(p) => setForm(f => ({ ...f, aadhar_front_path: p }))} onClear={() => setForm(f => ({ ...f, aadhar_front_path: '' }))} testid="aadhar-front" />
                <FileSlot label="Aadhar Back" path={form.aadhar_back_path} onUpload={(p) => setForm(f => ({ ...f, aadhar_back_path: p }))} onClear={() => setForm(f => ({ ...f, aadhar_back_path: '' }))} testid="aadhar-back" />
                <FileSlot label="PAN Card" path={form.pan_card_path} onUpload={(p) => setForm(f => ({ ...f, pan_card_path: p }))} onClear={() => setForm(f => ({ ...f, pan_card_path: '' }))} testid="pan-card" />
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button variant="outline" className="flex-1 h-11 border-[#E5E5EA] rounded-full text-sm" onClick={() => setDialog(null)}>Cancel</Button>
              <Button data-testid="save-account-btn" className="flex-1 h-11 bg-[#1C1C1E] text-white hover:bg-[#2C2C2E] rounded-full text-sm font-semibold"
                onClick={dialog === 'add' ? handleCreate : handleUpdate} disabled={submitting}>
                {submitting ? 'Saving...' : dialog === 'add' ? 'Create Account' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={dialog === 'password'} onOpenChange={() => setDialog(null)}>
        <DialogContent className="bg-white max-w-[92vw] sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Change Password</DialogTitle>
            <DialogDescription className="text-[13px] text-[#8E8E93]">Set a new password for @{editingAccount?.username}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-[11px] font-semibold uppercase tracking-wider text-[#8E8E93] mb-1.5 block">New Password</Label>
              <div className="relative">
                <Input data-testid="new-password-input" type={showPassword ? 'text' : 'password'} className="h-12 bg-[#F2F2F7] border-0 rounded-2xl text-[#1C1C1E] pr-12" value={form.password} onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))} placeholder="New password" />
                <button type="button" className="absolute right-4 top-1/2 -translate-y-1/2 text-[#8E8E93]" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" className="flex-1 h-12 border-[#E5E5EA] rounded-full text-sm" onClick={() => setDialog(null)}>Cancel</Button>
              <Button data-testid="save-password-btn" className="flex-1 h-12 bg-[#1C1C1E] text-white hover:bg-[#2C2C2E] rounded-full text-sm font-semibold" onClick={handleUpdate} disabled={submitting}>
                {submitting ? 'Changing...' : 'Change Password'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Extend Validity Dialog */}
      <Dialog open={dialog === 'extend'} onOpenChange={() => setDialog(null)}>
        <DialogContent className="bg-white max-w-[92vw] sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Extend Validity</DialogTitle>
            <DialogDescription className="text-[13px] text-[#8E8E93]">
              Add more months to @{editingAccount?.username}'s account.
              {editingAccount?.expires_at && <> Current expiry: <b>{new Date(editingAccount.expires_at).toLocaleDateString('en-IN')}</b></>}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-[11px] font-semibold uppercase tracking-wider text-[#8E8E93] mb-1.5 block">Add</Label>
              <div className="grid grid-cols-4 gap-2">
                {EXTEND_OPTIONS.map(m => (
                  <button key={m} data-testid={`extend-opt-${m}`} type="button"
                    className={`h-11 rounded-2xl text-sm font-medium border transition-colors ${extendMonths === String(m) ? 'bg-[#1C1C1E] text-white border-[#1C1C1E]' : 'bg-[#F2F2F7] text-[#1C1C1E] border-transparent'}`}
                    onClick={() => setExtendMonths(String(m))}>
                    +{m}mo
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" className="flex-1 h-11 border-[#E5E5EA] rounded-full text-sm" onClick={() => setDialog(null)}>Cancel</Button>
              <Button data-testid="confirm-extend-btn" className="flex-1 h-11 bg-[#1C1C1E] text-white hover:bg-[#2C2C2E] rounded-full text-sm font-semibold" onClick={handleExtend} disabled={submitting}>
                {submitting ? 'Extending...' : `Extend +${extendMonths}mo`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog open={dialog === 'view'} onOpenChange={() => setDialog(null)}>
        <DialogContent className="bg-white max-w-[94vw] sm:max-w-xl rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">{editingAccount?.name}</DialogTitle>
            <DialogDescription className="text-[13px] text-[#8E8E93]">@{editingAccount?.username} · {editingAccount?.role}</DialogDescription>
          </DialogHeader>
          {editingAccount && (
            <div className="space-y-4 mt-2">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><p className="text-[11px] font-semibold uppercase text-[#8E8E93] mb-0.5">Status</p>
                  <p className="text-[#1C1C1E]">{editingAccount.is_active ? 'Active' : 'On Hold'}</p></div>
                <div><p className="text-[11px] font-semibold uppercase text-[#8E8E93] mb-0.5">Expiry</p>
                  <p className="text-[#1C1C1E]">{editingAccount.expires_at ? new Date(editingAccount.expires_at).toLocaleDateString('en-IN') : 'Unlimited'}</p></div>
                <div><p className="text-[11px] font-semibold uppercase text-[#8E8E93] mb-0.5">Phone</p>
                  <p className="text-[#1C1C1E]">{editingAccount.phone || '—'}</p></div>
                <div><p className="text-[11px] font-semibold uppercase text-[#8E8E93] mb-0.5">PAN</p>
                  <p className="text-[#1C1C1E]">{editingAccount.pan_number || '—'}</p></div>
                <div className="col-span-2"><p className="text-[11px] font-semibold uppercase text-[#8E8E93] mb-0.5">Address</p>
                  <p className="text-[#1C1C1E] whitespace-pre-wrap">{editingAccount.address || '—'}</p></div>
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase text-[#8E8E93] mb-1.5">KYC Documents</p>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { label: 'Aadhar Front', path: editingAccount.aadhar_front_path },
                    { label: 'Aadhar Back', path: editingAccount.aadhar_back_path },
                    { label: 'PAN Card', path: editingAccount.pan_card_path },
                  ].map(({ label, path }) => (
                    <div key={label}>
                      <p className="text-[10px] text-[#8E8E93] mb-1">{label}</p>
                      {path ? (
                        <div className="aspect-[1.6] bg-[#F2F2F7] rounded-2xl overflow-hidden border border-[#E5E5EA]">
                          <img src={`${API}/files/${path}?auth=${localStorage.getItem('access_token')}`} alt={label} className="w-full h-full object-cover" />
                        </div>
                      ) : (
                        <div className="aspect-[1.6] bg-[#F7F7F7] border-2 border-dashed border-[#E5E5EA] rounded-2xl flex items-center justify-center text-[#C7C7CC] text-[10px]">Not uploaded</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              <Button variant="outline" className="w-full h-11 border-[#E5E5EA] rounded-full text-sm"
                onClick={() => { openEdit(editingAccount); }}>Edit Details</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
